import { MAX_PRINT_METHOD_NAME_LENGTH } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { useInvalidatePrintMethods, usePrintMethodsQuery } from '@/entities/print-method';
import {
  PrintMethodRow,
  useCreatePrintMethod,
  useDeletePrintMethod,
  useRenamePrintMethod,
  useSetPrintMethodDescription,
} from '@/features/print-methods';
import { type RenameTarget, resetSettledRequests } from '@/shared/lib';
import { Button, ContentCard, cn, EmptyState, NameFormModal, tableStyles } from '@/shared/ui';

/**
 * 인쇄 방식 마스터 — 와이어프레임의 표(방식명·설명·적용 상품 수·관리) 형태.
 * 설명은 고객 상품 상세의 프린트 가이드 본문이고, 적용 상품 수는 미사용 방식을 정리할 때의 판단 근거다.
 */
export const PrintMethodMasterSection = (): ReactElement => {
  const invalidate = useInvalidatePrintMethods();
  const methodsQuery = usePrintMethodsQuery();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null);
  const [descriptionTarget, setDescriptionTarget] = useState<{
    readonly id: string;
    readonly value: string;
  } | null>(null);

  const createAction = useCreatePrintMethod({
    onSuccess: () => {
      invalidate();
      setIsAddModalOpen(false);
    },
  });
  const renameAction = useRenamePrintMethod({
    onSuccess: () => {
      invalidate();
      setRenameTarget(null);
    },
  });
  const deleteAction = useDeletePrintMethod({ onSuccess: invalidate });
  const descriptionAction = useSetPrintMethodDescription({
    onSuccess: () => {
      invalidate();
      setDescriptionTarget(null);
    },
  });

  const mutationError = renameAction.error ?? descriptionAction.error ?? deleteAction.error;

  // 이름 수정·삭제는 오류 한 줄(mutationError)을 함께 쓴다(추가 오류는 추가 모달이 보여 준다). 새 요청을
  // 보내기 전에 두 요청의 지난 오류를 모두 지운다 — 아니면 다른 요청을 해도 옛 오류가 그 요청이 다시
  // 돌 때까지 남는다. 행에는 요청 사이의 잠금이 없어 다른 요청이 진행 중일 수 있다 — 진행 중인 요청은
  // 지우지 않는다.
  const clearMutationErrors = (): void => {
    resetSettledRequests([renameAction, descriptionAction, deleteAction]);
  };

  const rename = {
    target: renameTarget
      ? { ...renameTarget, isPending: renameAction.pendingId === renameTarget.id }
      : null,
    onChange: (name: string) =>
      setRenameTarget((current) => (current ? { ...current, name } : current)),
    onSubmit: () => {
      if (renameTarget?.name.trim()) {
        clearMutationErrors();
        renameAction.renamePrintMethod({ id: renameTarget.id, name: renameTarget.name.trim() });
      }
    },
    onCancel: () => setRenameTarget(null),
  };

  const description = {
    target: descriptionTarget
      ? { ...descriptionTarget, isPending: descriptionAction.pendingId === descriptionTarget.id }
      : null,
    onChange: (value: string) =>
      setDescriptionTarget((current) => (current ? { ...current, value } : current)),
    onSubmit: () => {
      if (descriptionTarget) {
        clearMutationErrors();
        // 비우면 null — 서버 계약이 빈 문자열을 null 로 바꾸지만 의도를 여기서도 분명히 한다.
        descriptionAction.setDescription({
          id: descriptionTarget.id,
          description: descriptionTarget.value.trim() || null,
        });
      }
    },
    onCancel: () => setDescriptionTarget(null),
  };

  const handleDelete = (method: { readonly id: string; readonly name: string }): void => {
    if (window.confirm(`인쇄 방식 "${method.name}" 을(를) 삭제하시겠습니까?`)) {
      clearMutationErrors();
      deleteAction.deletePrintMethod({ id: method.id });
    }
  };

  return (
    <ContentCard
      title="인쇄 방식"
      actions={
        <Button variant="outline" size="sm" onClick={() => setIsAddModalOpen(true)}>
          + 인쇄 방식 추가
        </Button>
      }
    >
      {mutationError ? (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {mutationError.message}
        </p>
      ) : null}

      {methodsQuery.isPending ? (
        <EmptyState>불러오는 중…</EmptyState>
      ) : methodsQuery.isError ? (
        <EmptyState isError>
          인쇄 방식을 불러오지 못했습니다: {methodsQuery.error.message}
        </EmptyState>
      ) : methodsQuery.data.length === 0 ? (
        <EmptyState>등록된 인쇄 방식이 없습니다.</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-foreground/[0.06]">
          <table className={tableStyles.table}>
            <thead className={tableStyles.head}>
              <tr>
                <th className={cn(tableStyles.th, 'w-48')}>인쇄 방식명</th>
                <th className={tableStyles.th}>설명 (고객 프린트 가이드)</th>
                <th className={cn(tableStyles.th, 'w-40')}>적용 상품 수</th>
                <th className={cn(tableStyles.th, 'w-48')}>관리</th>
              </tr>
            </thead>
            <tbody>
              {methodsQuery.data.map((method) => (
                <PrintMethodRow
                  key={method.id}
                  method={method}
                  rename={rename}
                  description={description}
                  onRenameStart={(target) => setRenameTarget({ id: target.id, name: target.name })}
                  onDescriptionStart={(target) =>
                    setDescriptionTarget({ id: target.id, value: target.description ?? '' })
                  }
                  onDelete={handleDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-muted-foreground">
        ⓘ 상품이 쓰고 있는 방식은 지울 수 없습니다 — 이름만 수정할 수 있습니다.
      </p>

      {isAddModalOpen ? (
        <NameFormModal
          title="인쇄 방식 추가"
          label="인쇄 방식명"
          placeholder="예: 실크스크린"
          maxLength={MAX_PRINT_METHOD_NAME_LENGTH}
          isPending={createAction.isPending}
          errorMessage={createAction.error?.message ?? null}
          onSubmit={(name) => createAction.createPrintMethod({ name })}
          onClose={() => setIsAddModalOpen(false)}
        />
      ) : null}
    </ContentCard>
  );
};
